import { STORAGE_KEYS, LANGUAGE_SETTINGS, IMAGE_PATHS } from '/settings.js';

// Current translations object (single language)
let translations = null;
// Cache for all loaded languages: { 'en': {...}, 'es': {...}, ... }
let translationsCache = {};
// Promises for loading languages: { 'en': Promise, 'es': Promise, ... }
let loadingPromises = {};

/**
 * Loads a specific language file from the domain-specific translations folder.
 * Results are cached to avoid reloading the same language.
 * @param {string} lang - The language code (e.g., 'en', 'es')
 * @returns {Promise<Object>} The translations object for that language
 */
async function loadLanguage(lang) {
    // If already cached, return immediately
    if (translationsCache[lang]) {
        return translationsCache[lang];
    }

    // If already loading, return the existing promise
    if (loadingPromises[lang]) {
        return loadingPromises[lang];
    }

    // Create new loading promise
    loadingPromises[lang] = fetch(`/translations/${lang}.json`)
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} for language: ${lang}`);
            }
            return response.json();
        })
        .then(data => {
            // Cache the translations
            translationsCache[lang] = data;
            // Clear the loading promise
            delete loadingPromises[lang];
            return data;
        })
        .catch(error => {
            console.error(`Error loading translations for ${lang}:`, error);
            // Clear the loading promise on error
            delete loadingPromises[lang];
            // Fallback to English if available
            if (lang !== 'en' && translationsCache['en']) {
                console.warn(`Falling back to English for ${lang}`);
                return translationsCache['en'];
            }
            throw new Error(`Failed to load translations for ${lang}`);
        });

    return loadingPromises[lang];
}

/**
 * Loads translations for the current language.
 * It fetches the file only once and caches the result.
 * @returns {Promise<Object>} The translations object for the current language.
 */
async function loadTranslations() {
    const currentLang = getCurrentLanguage();

    // If current language is already loaded, return it
    if (translations && translationsCache[currentLang] === translations) {
        return translations;
    }

    // Load the current language
    translations = await loadLanguage(currentLang);
    return translations;
}

/**
 * Detects the browser's preferred language
 * @returns {string} The detected language code or default fallback
 */
function detectBrowserLanguage() {
    // Get supported languages from settings
    const supportedLangs = LANGUAGE_SETTINGS.SUPPORTED_LANGUAGES;

    // Get browser language(s)
    const browserLangs = navigator.languages
        ? Array.from(navigator.languages)
        : [navigator.language];

    // Try to find a match
    for (const browserLang of browserLangs) {
        // Exact match (e.g., 'en' or 'es')
        if (supportedLangs.includes(browserLang)) {
            return browserLang;
        }

        // Partial match (e.g., 'en-US' → 'en', 'es-MX' → 'es')
        const langCode = browserLang.split('-')[0];
        if (supportedLangs.includes(langCode)) {
            return langCode;
        }
    }

    // Fallback to default if no match
    return LANGUAGE_SETTINGS.DEFAULT_LANGUAGE;
}

/**
 * Gets the current language from localStorage or returns default
 * @returns {string} The current language code
 */
export function getCurrentLanguage() {
    let lang = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (!lang) {
        lang = LANGUAGE_SETTINGS.DEFAULT_LANGUAGE;
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
    }
    // Validate language is supported
    if (!LANGUAGE_SETTINGS.SUPPORTED_LANGUAGES.includes(lang)) {
        lang = LANGUAGE_SETTINGS.DEFAULT_LANGUAGE;
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
    }
    return lang;
}

/**
 * Sets the current language and loads it dynamically
 * @param {string} lang - The language code to set
 * @param {boolean} [shouldReload=false] - Whether to reload the page (default: false for dynamic loading)
 */
export async function setLanguage(lang, shouldReload = false) {
    // Validate language
    if (!LANGUAGE_SETTINGS.SUPPORTED_LANGUAGES.includes(lang)) {
        console.warn(`Language ${lang} is not supported. Falling back to default.`);
        lang = LANGUAGE_SETTINGS.DEFAULT_LANGUAGE;
    }

    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);

    // Load the new language (will use cache if already loaded)
    try {
        translations = await loadLanguage(lang);
        // Dispatch event for other modules to react to language change
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lang } }));

        if (shouldReload) {
            // Reload the app to fully reset in the new language
            location.reload();
        }
    } catch (error) {
        console.error(`Failed to load language ${lang}:`, error);
        // Fallback to English if available
        if (translationsCache['en']) {
            translations = translationsCache['en'];
            localStorage.setItem(STORAGE_KEYS.LANGUAGE, 'en');
        }
    }
}

/**
 * Sets the language without reloading the page (for initial setup)
 * @param {string} lang - The language code to set
 */
export function setLanguageSilent(lang) {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
}

/**
 * Gets a translated string for the given key
 * @param {string} key - The translation key (e.g., 'buttons.zoomIn')
 * @param {string} [lang] - Optional language override
 * @returns {string} The translated string
 */
export function getText(key, lang = null) {
    if (!translations) {
        console.warn('Translations not loaded yet, returning key.');
        return key;
    }

    // If a specific language is requested, use it from cache if available
    let translationsToUse = translations;
    if (lang && lang !== getCurrentLanguage() && translationsCache[lang]) {
        translationsToUse = translationsCache[lang];
    }

    // Split the key into parts (e.g., 'buttons.zoomIn' -> ['buttons', 'zoomIn'])
    const keyParts = key.split('.');

    // Navigate through the translations object
    let result = translationsToUse;
    for (const part of keyParts) {
        if (result && typeof result === 'object' && result.hasOwnProperty(part)) {
            result = result[part];
        } else {
            const currentLang = lang || getCurrentLanguage();
            console.warn(`Translation key not found: ${key} for language: ${currentLang}`);
            return key; // Return the key if translation is not found
        }
    }

    return result;
}

/**
 * Updates all elements with data-i18n attribute with their translations
 */
export async function updatePageTranslations() {
    // Ensure translations are loaded before trying to update the page.
    if (!translations) {
        try {
            await loadTranslations();
        } catch (error) {
            console.error('Could not update page translations because loading failed.');
            return;
        }
    }

    const elements = document.querySelectorAll('[data-i18n]');
    elements.forEach(element => {
        const key = element.getAttribute('data-i18n');
        element.textContent = getText(key);
    });
}

/**
 * Initializes the language system
 */
export async function initializeLanguage() {
    // On first visit (no language in localStorage), detect browser language
    if (!localStorage.getItem(STORAGE_KEYS.LANGUAGE)) {
        const detectedLang = detectBrowserLanguage();
        localStorage.setItem(STORAGE_KEYS.LANGUAGE, detectedLang);
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log(`Browser language detected: ${detectedLang}`);
        }
    }

    // Load translations for the current language. This will be the single point of loading.
    try {
        await loadTranslations();
    } catch (error) {
        console.error('Initialization failed: Could not load translations.');
        // Optionally, display a user-friendly error message on the page
        return;
    }

    // Update initial translations now that they are guaranteed to be loaded
    await updatePageTranslations();

    // Listen for language changes
    window.addEventListener('languageChanged', async () => {
        // Reload translations for the new language and update the page
        try {
            await loadTranslations();
            await updatePageTranslations();
        } catch (error) {
            console.error('Failed to update translations after language change:', error);
        }
    });
}